import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { format, addDays, isAfter, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';
import utils from '../../utils';
import { notifyClassAttendees, notifyWaitlistForClass } from '../../database/databaseNotifications.js';
import { createUpdateCalendarClass, createCalendarClasses, deleteCalendarClass, fetchCalendarClasses } from '../../database/databaseCalendarClass.js';
import { cancelClassProfile, fetchClassProfiles, addClassProfile } from '../../database/databaseClassProfile.js';
import { fetchClasses } from '../../database/databaseClass.js';
import { fetchProfiles } from '../../database/databaseProfiles.js';

export default function ReserveModal({ open, onClose, slotInfo, onCreated }) {
  if (!open) return null;
  const isEditing = !!slotInfo.id;

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [addingProfile, setAddingProfile] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [addMode, setAddMode] = useState('single');
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'delete' | 'update'
  const [recurringInstances, setRecurringInstances] = useState([]);

  const [form, setForm] = useState({
    class: slotInfo.class || '',
    date: slotInfo.start,
    start: utils.toHHMM(slotInfo.start),
    end: utils.toHHMM(slotInfo.end),
    color: slotInfo.color || '#a855f7',
    comment: slotInfo.comment || '',
    maxattendees: slotInfo.maxattendees ?? '',
    repeat: false,
    repeatDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
    repeatEndDate: '',
    title: slotInfo.title || '',
  });

  useEffect(() => {
    const loadClasses = async () => {
      const data = await fetchClasses();
      setClasses(data || []);
      if (!slotInfo.id && data?.length > 0) {
        setForm((f) => ({
          ...f,
          class: data[0].id,
          maxattendees: data[0].maxattendees ?? '',
          color: data[0].color || f.color,
        }));
      }
      setLoading(false);
    };
    loadClasses();
  }, [slotInfo.id]);

  useEffect(() => {
    if (!isEditing || !slotInfo?.id) return;

    (async () => {
      setAttendeesLoading(true);
      const data = await fetchClassProfiles(slotInfo.id, false);
      setAttendees(data ?? []);
      setAttendeesLoading(false);
    })();
  }, [isEditing, slotInfo?.id]);

  useEffect(() => {
    if (!isEditing || !open) return;

    (async () => {
      setProfilesLoading(true);
      const data = await fetchProfiles();
      setAllProfiles(data ?? []);
      setProfilesLoading(false);
    })();
  }, [isEditing, open]);

  const reloadAttendees = async () => {
    const data = await fetchClassProfiles(slotInfo.id, false);
    setAttendees(data ?? []);
  };
  const attendingIds = new Set(attendees.map((a) => a.user_id));
  const availableProfiles = (allProfiles || []).filter((p) => !attendingIds.has(p.id));

  const handleAddProfile = async () => {
    setAddingProfile(true);

    const isWaitlist = slotInfo.confirmed_count >= slotInfo.maxattendees;

    if (addMode === 'single') {
      await addClassProfile(selectedProfileId, slotInfo.id, isWaitlist, null);
    }

    if (addMode === 'recurring') {
      const futureClasses = await fetchCalendarClasses();

      const slotStart = utils.getLocalHHMMSS(slotInfo.start);
      const slotEnd = utils.getLocalHHMMSS(slotInfo.end);

      const sameSchedule = futureClasses.filter(
        (c) =>
          c.class === slotInfo.class &&
          c.starttime === slotStart &&
          c.endtime === slotEnd &&
          new Date(c.date).getDay() === new Date(slotInfo.date).getDay()
      );

      for (const cls of sameSchedule) {
        await addClassProfile(selectedProfileId, cls.id, false, null);
      }
    }

    setSelectedProfileId('');
    await reloadAttendees();
    setAddingProfile(false);
  };

  const computeRepeatingDates = () => {
    if (!form.repeat) return [];

    const chosen = Object.entries(form.repeatDays)
      .filter(([, v]) => v)
      .map(([k]) => utils.DAY_TO_JS[k]);

    const startDate = new Date(form.date.getFullYear(), form.date.getMonth(), form.date.getDate());

    if (!form.repeatEndDate) return [];
    const [y, m, d] = form.repeatEndDate.split('-').map((n) => parseInt(n, 10));
    const endDate = new Date(y, m - 1, d);

    const selectedDays = chosen.length ? chosen : [startDate.getDay()];

    const dates = [];
    let cursor = new Date(startDate);
    while (!isAfter(cursor, endDate)) {
      if (selectedDays.includes(cursor.getDay())) {
        dates.push(new Date(cursor));
      }
      cursor = addDays(cursor, 1);
    }

    if (
      !dates.some((d2) => d2.getTime() === startDate.getTime()) &&
      !isBefore(endDate, startDate) &&
      selectedDays.includes(startDate.getDay())
    ) {
      dates.unshift(startDate);
    }

    return dates;
  };

  const findRecurringInstances = async () => {
    const origStart = utils.getLocalHHMMSS(slotInfo.start);
    const origDay = new Date(`${slotInfo.date}T00:00:00`).getDay();

    const { data, error } = await supabase
      .from('calendarclass')
      .select('id, class, date, starttime, endtime, color, comment, maxattendees')
      .eq('class', slotInfo.class)
      .eq('starttime', origStart)
      .neq('id', slotInfo.id);

    if (error || !data) return [];

    return data.filter((c) => new Date(`${c.date}T00:00:00`).getDay() === origDay);
  };

  const handleApplyAction = async (applyToAll) => {
    setShowRecurringDialog(false);
    const tStart = utils.toHHMMSS(form.start);
    const tEnd = utils.toHHMMSS(form.end);

    if (pendingAction === 'delete') {
      if (applyToAll) {
        for (const inst of recurringInstances) await deleteCalendarClass(inst.id);
      }
      await deleteCalendarClass(slotInfo.id);
    } else if (pendingAction === 'update') {
      if (applyToAll) {
        for (const inst of recurringInstances) {
          await createUpdateCalendarClass(inst.id, form.class, new Date(`${inst.date}T00:00:00`), tStart, tEnd, form.color, form.comment, form.maxattendees || null);
        }
      }
      await createUpdateCalendarClass(slotInfo.id, form.class, form.date, tStart, tEnd, form.color, form.comment, form.maxattendees || null);
      if (slotInfo.comment !== form.comment) {
        notifyClassAttendees(
          `<p>Clase: ${slotInfo.title} con fecha ${new Date(form.date).toLocaleDateString('es-ES')} y hora ${form.start} </p><p> ${form.comment}</p>`,
          slotInfo.id,
        );
      }
    }

    onClose();
    onCreated?.();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const tStart = utils.toHHMMSS(form.start);
    const tEnd = utils.toHHMMSS(form.end);

    if (isEditing) {
      const recurring = await findRecurringInstances();
      if (recurring.length > 0) {
        setRecurringInstances(recurring);
        setPendingAction('update');
        setShowRecurringDialog(true);
        return;
      }
      await createUpdateCalendarClass(slotInfo.id, form.class, form.date, tStart, tEnd, form.color, form.comment, form.maxattendees || null);
      if (slotInfo.comment !== form.comment) {
        notifyClassAttendees(
          `<p>Clase: ${slotInfo.title} con fecha ${new Date(form.date).toLocaleDateString('es-ES')} y hora ${form.start} </p><p> ${form.comment}</p>`,
          slotInfo.id,
        );
      }
      onClose();
      onCreated?.();
      return;
    }

    if (!form.repeat) {
      await createUpdateCalendarClass(null, form.class, form.date, tStart, tEnd, form.color, form.comment, form.maxattendees || null);
    } else {
      if (!form.repeatEndDate) {
        alert('Selecciona una fecha de fin para la repetición.');
        return;
      }
      const allDates = computeRepeatingDates();
      if (allDates.length === 0) {
        alert('No hay fechas que coincidan con los días seleccionados.');
        return;
      }
      const rows = allDates.map((d) => ({
        class: form.class,
        date: format(d, 'yyyy-MM-dd'),
        starttime: tStart,
        endtime: tEnd,
        color: form.color,
        comment: form.comment,
        maxattendees: form.maxattendees || null,
      }));
      await createCalendarClasses(rows);
    }

    onClose();
    onCreated?.();
  };

  useEffect(() => {
    setForm((f) => ({
      ...f,
      class: slotInfo.class ?? f.class,
      date: slotInfo.start,
      start: utils.toHHMM(slotInfo.start),
      end: utils.toHHMM(slotInfo.end),
      color: slotInfo.color ?? f.color,
      comment: slotInfo.comment ?? '',
      maxattendees: slotInfo.maxattendees ?? '',
    }));
  }, [open, slotInfo]);

  const handleDelete = async () => {
    if (!slotInfo.id) return;
    const recurring = await findRecurringInstances();
    if (recurring.length > 0) {
      setRecurringInstances(recurring);
      setPendingAction('delete');
      setShowRecurringDialog(true);
      return;
    }
    if (confirm('¿Estás seguro de que deseas eliminar esta clase?')) {
      await deleteCalendarClass(slotInfo.id);
      onClose();
      onCreated?.();
    }
  };

  const handleRemove = async (classProfileId) => {
    if (!classProfileId) return;
    setRemovingId(classProfileId);

    const record = await cancelClassProfile(classProfileId);
    setAttendees((list) => list.filter((a) => a.id !== classProfileId));

    if (record && !record.waiting_list) {
      const dateTime = format(slotInfo.start, "EEEE, d 'de' MMMM yyyy – HH:mm", { locale: es });
      notifyWaitlistForClass(record.calendar_class, slotInfo.title, dateTime);
    }

    setRemovingId(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-[2%]">
      <div className="w-[96%] sm:w-[85%] md:w-[70%] lg:w-[50%] xl:w-[40%] max-w-[900px] rounded-lg bg-card p-[4%] shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="mb-[2%] text-xl font-serif text-terracotta">{isEditing ? 'Editar reserva' : 'Crear reserva'}</h2>

        <p className="mb-[3%] text-sm text-ink/70">{format(slotInfo.start, "EEEE, d 'de' MMMM yyyy – HH:mm", { locale: es })}</p>

        <form className="space-y-[3%]" onSubmit={handleCreate}>
          {/* Clase */}
          <div>
            <label className="mb-[1%] block text-sm font-medium">Clase</label>
            <select
              value={form.class}
              onChange={(e) => {
                const selectedClass = classes.find((c) => c.id === Number(e.target.value));
                setForm({
                  ...form,
                  class: Number(e.target.value),
                  maxattendees: selectedClass?.maxattendees ?? '',
                  color: selectedClass?.color || form.color,
                });
              }}
              required
              className="w-full rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%]"
            >
              {loading ? (
                <option disabled>Cargando…</option>
              ) : (
                classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Horas */}
          <div className="grid grid-cols-2 gap-[3%]">
            <div>
              <label className="mb-[1%] block text-sm font-medium">Inicio</label>
              <input
                type="time"
                step="60"
                inputMode="numeric"
                value={form.start}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    start: e.target.value,
                    end: utils.addOneHour(e.target.value),
                  }))
                }
                required
                className="w-full rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%]"
              />
            </div>
            <div>
              <label className="mb-[1%] block text-sm font-medium">Fin</label>
              <input
                type="time"
                step="60"
                inputMode="numeric"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                required
                className="w-full rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%]"
              />
            </div>
          </div>

          {/* Color y Max. asistentes */}
          <div className="grid grid-cols-2 gap-[3%]">
            <div>
              <label className="mb-[1%] block text-sm font-medium">Color</label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-full h-10 p-[1%] border border-sand bg-cream rounded"
              />
            </div>
            <div>
              <label className="mb-[1%] block text-sm font-medium">Max. asistentes</label>
              <input
                type="number"
                min="1"
                value={form.maxattendees}
                onChange={(e) => setForm({ ...form, maxattendees: e.target.value ? Number(e.target.value) : '' })}
                className="w-full h-10 rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%]"
                placeholder="Sin límite"
              />
            </div>
          </div>

          {/* Comentario */}
          <div>
            <label className="mb-[1%] block text-sm font-medium">Comentario</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              className="w-full rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%] min-h-[120px]"
            />
          </div>

          {/* Repetir */}
          {!isEditing && (
            <>
              <label className="flex items-center gap-[2%]">
                <input type="checkbox" checked={form.repeat} onChange={() => setForm((prev) => ({ ...prev, repeat: !prev.repeat }))} />
                Repetir
              </label>

              {form.repeat && (
                <div className="space-y-[2%]">
                  <label className="mb-[1%] block text-sm font-medium">Días de la semana</label>
                  <div className="flex flex-wrap gap-[3%]">
                    {utils.DAY_KEYS.map((day) => (
                      <label key={day} className="flex items-center gap-[6px]">
                        <input
                          type="checkbox"
                          checked={form.repeatDays[day]}
                          onChange={() =>
                            setForm((prev) => ({
                              ...prev,
                              repeatDays: {
                                ...prev.repeatDays,
                                [day]: !prev.repeatDays[day],
                              },
                            }))
                          }
                        />
                        <span className="capitalize">{utils.DAY_LABELS_ES[day]}</span>
                      </label>
                    ))}
                  </div>

                  <label className="mb-[1%] block text-sm font-medium">Fecha de fin</label>
                  <input
                    type="date"
                    value={form.repeatEndDate}
                    onChange={(e) => setForm({ ...form, repeatEndDate: e.target.value })}
                    className="w-full rounded border border-sand bg-cream text-ink px-[2.5%] py-[1.5%]"
                    required
                  />
                  <p className="text-xs text-ink/60">Si no seleccionas días, se repetirá el mismo día de la semana que la fecha de inicio.</p>
                </div>
              )}
            </>
          )}

          {/* Botones */}
          <div className="flex justify-between pt-[3%]">
            {isEditing && (
              <button type="button" onClick={handleDelete} className="rounded bg-red-500 px-[4%] py-[2%] text-white hover:bg-red-600">
                Eliminar
              </button>
            )}
            <div className="flex gap-[2%] w-full sm:w-auto sm:justify-end sm:gap-[2%]">
              <button type="button" onClick={onClose} className="rounded border border-sand text-ink hover:bg-sand/40 px-[4%] py-[2%] w-[48%] sm:w-auto transition">
                Cancelar
              </button>
              <button type="submit" className="rounded bg-terracotta px-[4%] py-[2%] text-cream hover:bg-terracotta-dark w-[48%] sm:w-auto">
                {isEditing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>

          {/* Asistentes (solo edición) */}
          {isEditing && (
            <div className="mt-4 space-y-3">
              <h3 className="text-sm font-serif text-terracotta">Usuarios en esta clase</h3>

              {attendeesLoading ? (
                <p className="text-sm text-ink/60">Cargando…</p>
              ) : attendees.length === 0 ? (
                <p className="text-sm text-ink/60">No hay usuarios apuntados.</p>
              ) : (
                <ul className="divide-y rounded border border-sand">
                  {attendees.map((a) => (
                    <li key={a.id} className="flex items-center justify-between p-2">
                      <div className="font-medium">{a.profiles?.full_name}</div>
                      {a.waiting_list
                        ? <span className="text-xs font-medium text-orange-500">Lista de espera</span>
                        : <span className="text-yellow-500">★</span>
                      }
                      {a.canceled_at && <span className="text-sm text-ink/60">Cancelado el {format(new Date(a.canceled_at), 'dd/MM/yyyy')}</span>}
                      <button
                        type="button"
                        onClick={() => handleRemove(a.id)}
                        disabled={removingId === a.id}
                        className="rounded bg-red-500 px-3 py-1 text-white hover:bg-red-600 disabled:opacity-50"
                      >
                        {removingId === a.id ? 'Quitando…' : 'Quitar'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex flex-col gap-2">
                <select
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                  disabled={profilesLoading || availableProfiles.length === 0}
                  className="rounded border border-sand bg-cream text-ink px-3 py-2"
                >
                  <option value="">
                    {profilesLoading ? 'Cargando perfiles…' : availableProfiles.length ? 'Selecciona un perfil' : 'No hay perfiles disponibles'}
                  </option>
                  {availableProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name || `Perfil ${p.id}`}
                    </option>
                  ))}
                </select>

                <select value={addMode} onChange={(e) => setAddMode(e.target.value)} className="rounded border border-sand bg-cream text-ink px-3 py-2 text-sm">
                  <option value="single">Solo para esta clase</option>
                  <option value="recurring">Fijo en este horario (todas las clases)</option>
                </select>

                <button
                  type="button"
                  onClick={handleAddProfile}
                  disabled={!selectedProfileId || addingProfile}
                  className="rounded bg-terracotta px-4 py-2 text-cream hover:bg-terracotta-dark disabled:opacity-50"
                >
                  {addingProfile ? 'Agregando…' : 'Agregar'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Diálogo clases recurrentes */}
      {showRecurringDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-serif text-terracotta mb-2">Clase recurrente</h3>
            <p className="text-ink/70 text-sm mb-5">
              Esta clase tiene <strong>{recurringInstances.length}</strong> instancia{recurringInstances.length !== 1 ? 's' : ''} con el mismo horario y día de la semana.
              ¿Querés aplicar el cambio solo a esta clase o a todas?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleApplyAction(false)}
                className="w-full rounded bg-terracotta text-cream py-2 hover:bg-terracotta-dark transition"
              >
                Solo esta clase
              </button>
              <button
                onClick={() => handleApplyAction(true)}
                className="w-full rounded bg-red-600 text-white py-2 hover:bg-red-700 transition"
              >
                Todas las clases recurrentes ({recurringInstances.length + 1})
              </button>
              <button
                onClick={() => setShowRecurringDialog(false)}
                className="w-full rounded border border-sand text-ink/70 py-2 hover:bg-sand/40 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}